// contexts/NotificationContext.js
import React, { createContext, useContext, useEffect } from "react";
import notificationService from "../services/notificationService";
import { useUser } from "./UserContext";

/**
 * NotificationContext
 * Provides global access to notification functionality throughout the application
 */
const NotificationContext = createContext();

/**
 * NotificationProvider component
 * Wraps the application to provide notification functionality
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export const NotificationProvider = ({ children }) => {
  const { user } = useUser();

  // Initialize notification service when user logs in
  useEffect(() => {
    if (user?.uid) {
      // Start listening for notifications when user logs in
      notificationService.startListening(user.uid);

      // Set up periodic cleanup of expired notifications
      const cleanupInterval = setInterval(() => {
        notificationService.cleanupExpiredNotifications(user.uid);
      }, 60 * 60 * 1000); // Run every hour

      return () => {
        clearInterval(cleanupInterval);
        notificationService.stopListening();
      };
    }
  }, [user]);

  /**
   * Send a notification to the current user
   * @param {string} title - Notification title
   * @param {string} message - Notification message
   * @param {string} type - Notification type (success, error, warning, info)
   * @param {string} link - Optional link
   * @returns {Promise<Object>} Created notification
   */
  const sendNotification = async (
    title,
    message,
    type = "info",
    link = null
  ) => {
    if (!user?.uid) return null;

    try {
      return await notificationService.createNotification(user.uid, {
        title,
        message,
        type,
        link,
      });
    } catch (error) {
      console.error("Error sending notification:", error);
      return null;
    }
  };

  /**
   * Send a notification to a specific user (admin only)
   * @param {string} userId - Target user ID
   * @param {string} title - Notification title
   * @param {string} message - Notification message
   * @param {string} type - Notification type (success, error, warning, info)
   * @param {string} link - Optional link
   * @returns {Promise<Object>} Created notification
   */
  const sendNotificationToUser = async (
    userId,
    title,
    message,
    type = "info",
    link = null
  ) => {
    if (!userId) return null;

    try {
      return await notificationService.createNotification(userId, {
        title,
        message,
        type,
        link,
      });
    } catch (error) {
      console.error("Error sending notification to user:", error);
      return null;
    }
  };

  /**
   * Create a social-style notification with user avatar
   * @param {string} userName - User name
   * @param {string} userAvatar - User avatar URL
   * @param {string} action - Action description
   * @param {string} message - Notification message
   * @param {string} link - Optional link
   * @returns {Promise<Object>} Created notification
   */
  const sendSocialNotification = async (
    userName,
    userAvatar,
    action,
    message,
    link = null
  ) => {
    if (!user?.uid) return null;

    try {
      return await notificationService.createSocialNotification(user.uid, {
        userName,
        userAvatar,
        action,
        message,
        link,
      });
    } catch (error) {
      console.error("Error sending social notification:", error);
      return null;
    }
  };

  const value = {
    sendNotification,
    sendNotificationToUser,
    sendSocialNotification,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

/**
 * useNotifications hook
 * Provides access to notification functionality
 * @returns {Object} Notification methods
 */
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider"
    );
  }
  return context;
};
